import React, { useState } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Container,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  Divider
} from '@mui/material';
import {
  Business,
  AccountBalance,
  Receipt,
  TrendingUp,
  Inventory,
  Calculate,
  Storage,
  Notifications,
  Print,
  Settings as SettingsIcon,
  People as PeopleIcon,
  MoreVert
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import CompanyInfoTab from './components/CompanyInfoTab';
import BanksManagementTab from './components/BanksManagementTab';
import TaxSettingsTab from './components/TaxSettingsTab';
import FiscalYearTab from './components/FiscalYearTab';
import InventorySettingsTab from './components/InventorySettingsTab';
import AccountingSettingsTab from './components/AccountingSettingsTab';
import DatabaseSettingsTab from './components/DatabaseSettingsTab';
import NotificationsTab from './components/NotificationsTab';
import PrintSettingsTab from './components/PrintSettingsTab';
import SystemSettingsTab from './components/SystemSettingsTab';
import HRSettingsTab from './components/HRSettingsTab';
import XbrlSettingsTab from './components/XbrlSettingsTab';
import { Public as PublicIcon } from '@mui/icons-material';

const translations = {
  ar: {
    companyInfo: 'معلومات الشركة',
    banks: 'البنوك',
    tax: 'الضرائب',
    fiscalYear: 'السنوات المالية',
    inventory: 'المخزون',
    hr: 'الموظفين والدوام',
    accounting: 'المحاسبة',
    database: 'قاعدة البيانات',
    notifications: 'الإشعارات',
    print: 'الطباعة',
    system: 'النظام',
    xbrl: 'الوسم XBRL',
    more: 'المزيد'
  },
  en: {
    companyInfo: 'Company Info',
    banks: 'Banks',
    tax: 'Tax',
    fiscalYear: 'Fiscal Year',
    inventory: 'Inventory',
    hr: 'HR & Attendance',
    accounting: 'Accounting',
    database: 'Database',
    notifications: 'Notifications',
    print: 'Print',
    system: 'System',
    xbrl: 'XBRL',
    more: 'More'
  }
};

export default function Settings() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // تحديد عدد التابات حسب حجم الشاشة
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.down('lg'));

  const allTabs = [
    { label: t.companyInfo, icon: <Business />, component: <CompanyInfoTab /> },
    { label: t.banks, icon: <AccountBalance />, component: <BanksManagementTab /> },
    { label: t.tax, icon: <Receipt />, component: <TaxSettingsTab /> },
    { label: t.fiscalYear, icon: <TrendingUp />, component: <FiscalYearTab /> },
    { label: t.inventory, icon: <Inventory />, component: <InventorySettingsTab /> },
    { label: t.hr, icon: <PeopleIcon />, component: <HRSettingsTab /> },
    { label: t.accounting, icon: <Calculate />, component: <AccountingSettingsTab /> },
    { label: t.database, icon: <Storage />, component: <DatabaseSettingsTab /> },
    { label: t.notifications, icon: <Notifications />, component: <NotificationsTab /> },
    { label: t.print, icon: <Print />, component: <PrintSettingsTab /> },
    { label: t.system, icon: <SettingsIcon />, component: <SystemSettingsTab /> },
    { label: t.xbrl, icon: <PublicIcon />, component: <XbrlSettingsTab /> }
  ];

  // تحديد عدد التابات المعروضة حسب حجم الشاشة (محدث ومخفض)
  const getVisibleTabsCount = () => {
    if (isXs) return 2; // موبايل: 2 تابات فقط + قائمة المزيد
    if (isSm) return 3; // تابلت صغير: 3 تابات
    if (isMd) return 5; // تابلت كبير: 5 تابات
    return 6; // ديسكتوب: 6 تابات
  };

  const visibleTabsCount = getVisibleTabsCount();
  const visibleTabs = allTabs.slice(0, visibleTabsCount);
  const hiddenTabs = allTabs.slice(visibleTabsCount);
  const hasMoreTabs = hiddenTabs.length > 0;

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setAnchorEl(null);
  };

  const handleHiddenTabClick = (index: number) => {
    setActiveTab(visibleTabsCount + index);
    handleMoreClose();
  };

  return (
    <Container 
      maxWidth={false}
      disableGutters
      sx={{ 
        height: '100%',
        px: { xs: 1, sm: 2, md: 3 },
        overflow: 'hidden'
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <Box 
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider', 
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden'
        }}
      >
        <Tabs
          value={activeTab < visibleTabsCount ? activeTab : false}
          onChange={(_e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            flex: 1,
            minHeight: 64,
            overflow: 'hidden',
            '& .MuiTabs-flexContainer': {
              gap: { xs: 0, sm: 0.5, md: 1 }
            },
            '& .MuiTabs-indicator': {
              height: 4,
              borderRadius: '4px 4px 0 0',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            },
            '& .MuiTab-root': {
              minHeight: 64,
              textTransform: 'none',
              fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
              fontWeight: 500,
              minWidth: { xs: 'auto', sm: 100, md: 120 },
              px: { xs: 1, sm: 1.5, md: 2 },
              '&:hover': {
                color: 'primary.main',
                bgcolor: 'rgba(102, 126, 234, 0.05)'
              },
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700
              },
              '& .MuiTab-iconWrapper': {
                mr: { xs: 0, sm: 1 }
              }
            }
          }}
        >
          {visibleTabs.map((tab, i) => (
            <Tab 
              key={i} 
              icon={tab.icon} 
              label={isXs ? undefined : tab.label}
              iconPosition="start"
            />
          ))}
        </Tabs>

        {/* زر المزيد للتابات المخفية */}
        {hasMoreTabs && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <IconButton
              onClick={handleMoreClick}
              sx={{
                minWidth: 64,
                height: 64,
                borderRadius: 0,
                color: activeTab >= visibleTabsCount ? 'primary.main' : 'text.secondary',
                bgcolor: activeTab >= visibleTabsCount ? 'rgba(102, 126, 234, 0.05)' : 'transparent',
                '&:hover': {
                  bgcolor: 'rgba(102, 126, 234, 0.1)'
                }
              }}
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMoreClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: isRTL ? 'left' : 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: isRTL ? 'left' : 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                  maxHeight: 400
                }
              }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {t.more}
                </Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
              {hiddenTabs.map((tab, i) => (
                <MenuItem
                  key={i}
                  onClick={() => handleHiddenTabClick(i)}
                  selected={activeTab === visibleTabsCount + i}
                  sx={{
                    py: 1.5,
                    gap: 2,
                    '&.Mui-selected': {
                      bgcolor: 'rgba(102, 126, 234, 0.1)',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'rgba(102, 126, 234, 0.15)'
                      }
                    }
                  }}
                >
                  <Box sx={{ color: 'inherit', display: 'flex' }}>
                    {tab.icon}
                  </Box>
                  <Typography variant="body2">{tab.label}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
      
      <Box sx={{ overflowY: 'auto', overflowX: 'hidden' }}>
        {allTabs[activeTab].component}
      </Box>
    </Container>
  );
}